import React, { useEffect, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';

/**
 * YUMIX — Old Domain Migration Banner (Iter. 405)
 *
 * Some early beta testers installed the PWA from the legacy preview hosts:
 *   • meal-insights-pro.emergent.host
 *   • meal-insights-pro.preview.emergentagent.com
 *   • *.emergentcf.cloud  (Cloudflare-edged preview clusters)
 *
 * That installed PWA keeps hitting the old preview origin even after we
 * shipped extramiles.ca, so users miss new features and occasionally see
 * stale data. This banner detects the legacy host and nudges them to
 * reinstall from the production domain.
 *
 * Dismiss is persisted in localStorage for 30 days so we don't nag.
 */
const PROD_HOST = 'extramiles.ca';
// Specific legacy hosts that real users installed as PWA before extramiles.ca
// went live. Active dev/preview hosts (per-job `*.preview.emergentagent.com`)
// are deliberately NOT included — they would show the banner to internal
// testers, which is noise.
const LEGACY_HOST_PATTERNS = [
  /^meal-insights-pro\.emergent\.host$/i,
  /^meal-insights-pro\.emergent\.cloud$/i,
  /^meal-insights-pro\..*\.deploy\.emergentcf\.cloud$/i,
  /^meal-insights-pro\..*\.emergentcf\.cloud$/i,
];
const DISMISS_STORAGE_KEY = 'yumix:old-domain-banner-dismissed-at';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isLegacyHost(hostname) {
  if (!hostname) return false;
  if (hostname === PROD_HOST) return false;
  if (hostname === 'www.extramiles.ca') return false;
  // Localhost / 127.0.0.1 / *.preview.emergentagent.com in dev should not show banner
  if (hostname === 'localhost' || hostname === '127.0.0.1') return false;
  return LEGACY_HOST_PATTERNS.some((rx) => rx.test(hostname));
}

function isDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch (_e) {
    return false;
  }
}

export default function OldDomainMigrationBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = window.location?.hostname || '';
    if (isLegacyHost(host) && !isDismissedRecently()) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
    } catch (_e) {
      // localStorage unavailable (private mode / quota) → just hide for this session.
    }
    setVisible(false);
  };

  return (
    <div
      data-testid="old-domain-migration-banner"
      className="sticky top-0 z-[60] w-full bg-gradient-to-r from-amber-500/95 via-orange-500/95 to-amber-500/95 text-white shadow-lg backdrop-blur-md"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-snug">
            Tu utilises l'ancien aperçu de YUMIX
          </p>
          <p className="text-xs opacity-90 leading-snug">
            Pour la dernière version, réinstalle l'app depuis{' '}
            <a
              href={`https://${PROD_HOST}`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold inline-flex items-center gap-1"
              data-testid="old-domain-migration-cta"
            >
              {PROD_HOST}
              <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 p-1.5 rounded-full hover:bg-white/20 active:bg-white/30 transition"
          aria-label="Ignorer"
          data-testid="old-domain-migration-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Exported for unit testing
export const __testing = { isLegacyHost, isDismissedRecently, LEGACY_HOST_PATTERNS, PROD_HOST };
